import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  getOrgConfigForTenant as getOrgConfigForTenantQuery,
  getOrgConfigForWorkspace as getOrgConfigForWorkspaceQuery,
  updateOrgConfigForTenant as updateOrgConfigForTenantMutation,
  updateOrgConfigForWorkspace as updateOrgConfigForWorkspaceMutation,
} from "src/queries/orgConfig.graphql";
import { omitTypename } from "src/util/graphql";
import { getGraphQLErrors } from "src/util/error";

type Scope = "tenant" | "workspace";

const queries = {
  tenant: {
    getQuery: getOrgConfigForTenantQuery,
    updateMutation: updateOrgConfigForTenantMutation,
  },
  workspace: {
    getQuery: getOrgConfigForWorkspaceQuery,
    updateMutation: updateOrgConfigForWorkspaceMutation,
  },
} as const;

type ConfigRow = {
  config?: { customerContext?: string | null };
};

export const useCustomerContext = (scope: Scope) => {
  const { getQuery, updateMutation } = queries[scope];
  const { data, loading } = useQuery(getQuery, {
    fetchPolicy: "cache-first",
  });
  const [updateConfig, { loading: updating }] = useMutation(updateMutation);

  const row = useMemo(
    () =>
      omitTypename((data as { config?: ConfigRow } | undefined)?.config) as
        | ConfigRow
        | undefined,
    [data]
  );

  const update = useCallback(
    async (text: string) => {
      try {
        await updateConfig({
          variables: { input: { config: { customerContext: text } } },
          update: (cache, { data: updateData }) => {
            const next = (
              updateData as { config?: ConfigRow } | null | undefined
            )?.config;
            if (!next) return;
            const existingData = cache.readQuery({ query: getQuery });
            if (existingData) {
              cache.writeQuery({
                query: getQuery,
                data: { config: next },
              });
            }
          },
        });
      } catch (error) {
        const gqlErrors = getGraphQLErrors(error);
        const message = gqlErrors[0]?.message;
        if (message) {
          throw new Error(message);
        }
        throw error;
      }
    },
    [getQuery, updateConfig]
  );

  return {
    loading,
    customerContext: row?.config?.customerContext ?? "",
    update,
    updating,
  };
};
